import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Search, Globe, MapPin, Phone, Clock, Share, Scissors, Gift, Bell, History, Users, MoreVertical, LogIn } from 'lucide-react';
import { ShopLogo, ShopName } from '../components/ShopLogo';
import GoogleWalkthrough from '../components/GoogleWalkthrough';
import { getWebsiteConfig, WebsiteConfig, DEFAULT_CONFIG } from '../services/websiteConfigService';
import { publicHoursService } from '../services/publicHoursService';

/**
 * /app — the launch "get ready" hub (Jose 2026-10-08). Pre-launch: loud, declarative, benefit-first.
 * Book-first (guest, no account) up top; app sold on its BENEFITS (SLIK Rewards + Family booking).
 * Gradient-wrapped blocks (copper→terracotta, family=green), big type, pulsing launch banner.
 * Auto-flips to "we're live" on Oct 19. book.slikbarberlounge.com/app → slikbarberlounge.com/app post-cutover.
 */

const LAUNCH = new Date(2026, 9, 19, 0, 0, 0); // LIVE 2026-10-19 — hero now shows the "WE'RE LIVE" state
const APP_STORE_URL = 'https://apps.apple.com/app/id6819118327';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.slik.portal';
const PORTAL_URL = 'https://my.slikbarberlounge.com'; // the client portal — sign in / claim / save-to-home ("be remembered")

function useCountdown(target: Date) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const diff = Math.max(0, target.getTime() - now);
  return {
    live: diff === 0,
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

/** Gradient-border wrapper (copper→terracotta by default; pass green for the family block). */
const GradWrap: React.FC<{ grad?: string; glow?: boolean; children: React.ReactNode }> = ({ grad = 'from-slik-purple to-slik-magenta', glow, children }) => (
  <div className={`rounded-2xl p-[3px] bg-gradient-to-r ${grad} shadow-card`} style={glow ? { animation: 'slikGlow 1.6s ease-in-out infinite' } : undefined}>
    <div className="rounded-[13px] bg-white overflow-hidden text-left">{children}</div>
  </div>
);

/** The labeled header bar used on each block (same style across all, per Jose). */
const LabelBar: React.FC<{ grad?: string; children: React.ReactNode }> = ({ grad = 'from-slik-purple to-slik-magenta', children }) => (
  <div className={`bg-gradient-to-r ${grad} px-4 py-2.5 text-center`}>
    <span className="text-sm font-extrabold uppercase tracking-widest text-white">{children}</span>
  </div>
);

const AppLandingPage: React.FC = () => {
  const [config, setConfig] = useState<WebsiteConfig>(DEFAULT_CONFIG);
  const [hours, setHours] = useState<{ label: string; value: string }[]>([]);
  const [showGoogle, setShowGoogle] = useState(false); // Way-1 (Google) animated walkthrough
  const { live, d, h, m, s } = useCountdown(LAUNCH);

  useEffect(() => {
    getWebsiteConfig().then(setConfig).catch(() => {});
    publicHoursService.getFormattedHours().then(setHours).catch(() => {});
    document.title = 'Book SLIK Barber Lounge — faster, with reminders + rewards';
  }, []);

  const phone = config.shopInfo?.phone || '';
  const unit = (n: number, l: string) => (
    <div className="flex flex-col items-center">
      <span className="text-3xl font-extrabold text-ink tabular-nums leading-none">{String(n).padStart(2, '0')}</span>
      <span className="text-[11px] uppercase tracking-wide text-gray-400 mt-1">{l}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream">
      <style>{`@keyframes slikGlow{0%,100%{box-shadow:0 3px 10px rgba(0,0,0,.12),0 0 0 0 rgba(156,95,40,.75)}50%{box-shadow:0 3px 10px rgba(0,0,0,.12),0 0 0 18px rgba(156,95,40,0)}}@keyframes slikPop{0%,100%{transform:scale(1)}50%{transform:scale(1.14)}}`}</style>

      {/* HERO */}
      <div className="bg-gradient-to-b from-white to-cream px-5 pt-10 pb-8">
        <div className="max-w-md mx-auto text-center">
          <ShopLogo className="w-16 h-16 object-contain rounded-2xl mx-auto mb-3" />
          <ShopName nameClassName="text-xl font-extrabold text-ink" taglineClassName="text-xs text-gray-500" />
          <div className="mt-4 text-xs font-extrabold uppercase tracking-widest text-slik-purple">Introducing the SLIK app</div>
          <h1 className="mt-1 text-4xl font-extrabold text-ink leading-tight">The best way to book your cut</h1>
          <p className="mt-2 text-base font-semibold text-gray-700">Built by SLIK, owned by SLIK — made just for our clients.</p>

          {/* THE #1 MESSAGE (Jose 2026-10-09): Fresha is going away. Loud, blunt, impossible to miss —
              this is what the main audience (current Fresha bookers) MUST understand. Auto-flips at launch. */}
          {live ? (
            <div className="mt-6 rounded-2xl p-[3px] bg-gradient-to-r from-slik-green to-emerald-600">
              <div className="rounded-[13px] bg-white px-6 py-5 text-center">
                <span className="text-2xl font-black text-slik-green">● WE’RE LIVE — BOOK HERE NOW</span>
                <p className="mt-2 text-base font-bold text-ink">Fresha is <u>no longer</u> used — book SLIK on the <b>app</b>, the <b>site</b>, or <b>Google</b>. 👇</p>
              </div>
            </div>
          ) : (
            <GradWrap glow>
              <div className="p-5 text-center">
                <div className="inline-flex items-center gap-1.5 text-sm font-black uppercase tracking-widest text-red-600 bg-red-50 rounded-full px-4 py-1.5">⚠️ Important</div>
                <h2 className="mt-3 text-[34px] leading-[1.02] font-black text-ink uppercase">Fresha is being<br /><span className="text-red-600">discontinued</span></h2>
                <p className="mt-4 text-lg font-extrabold text-ink leading-snug">After <span className="text-slik-purple">Monday · Oct&nbsp;19</span>, Fresha will <u>NO LONGER</u> be used to book your SLIK appointments.</p>
                <p className="mt-2 text-base font-semibold text-gray-700 leading-snug">From <b>October 19 on</b>, you’ll book SLIK <b>directly</b> — on the app, the site, or Google. Same shop, same barbers, just better.</p>
                <div className="mt-5 flex items-center justify-center gap-2 text-red-600 font-black text-sm uppercase tracking-wide"><Clock className="w-5 h-5" /> Fresha ends in</div>
                <div className="mt-2 flex items-center justify-center gap-5">{unit(d, 'days')}{unit(h, 'hrs')}{unit(m, 'min')}{unit(s, 'sec')}</div>
                <p className="mt-5 text-xl font-extrabold text-ink leading-snug">👇 Get set up now — takes a minute</p>
              </div>
            </GradWrap>
          )}
        </div>
      </div>

      {/* WHY THE APP — benefits, sold loud */}
      <div className="px-5 pb-10">
        <div className="max-w-md mx-auto space-y-5">
          <div className="text-center mt-2">
            <h2 className="text-2xl font-extrabold text-ink">3 easy ways to book SLIK</h2>
            <p className="text-sm font-semibold text-gray-500 mt-1">Pick whatever’s easiest for you.</p>
          </div>

          {/* WAY 1 · GET THE APP — FIRST, because the app user is the primary audience (Jose 2026-10-09).
              Gold = coins/rewards theme. */}
          <GradWrap grad="from-amber-500 to-orange-600">
            <LabelBar grad="from-amber-500 to-orange-600">Way 1 · Get the App</LabelBar>
            <div className="p-5">
              <div className="flex items-center gap-2 text-ink font-extrabold text-xl"><Gift className="w-6 h-6 text-amber-600 shrink-0" /> SLIK Rewards — coins → free cuts</div>
              <div className="mt-3 flex items-start gap-2 bg-slik-green/10 border border-slik-green/50 rounded-xl px-3 py-2.5">
                <Users className="w-7 h-7 text-slik-green shrink-0" />
                <p className="text-sm font-bold text-ink"><span className="text-slik-green">Family booking</span> — book the whole family’s cuts in <b>one quick booking</b>.</p>
              </div>
              <div className="mt-3 flex items-center gap-3 text-base font-semibold text-ink"><Bell className="w-5 h-5 text-amber-600 shrink-0" /> Reminders + your full visit history</div>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-ink text-white text-base font-bold hover:opacity-90"> App Store</a>
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-ink text-white text-base font-bold hover:opacity-90">▶ Google Play</a>
              </div>
            </div>
          </GradWrap>

          {/* WAY 2 · GOOGLE */}
          <GradWrap grad="from-blue-500 to-blue-600">
            <LabelBar grad="from-blue-500 to-blue-600">Way 2 · On Google</LabelBar>
            <div className="p-5">
              <div className="flex items-start gap-3">
                <Search className="w-8 h-8 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-base font-semibold text-ink leading-snug">Search <b>“SLIK Barber Lounge”</b> on Google → tap the <b>Book</b> button → pick your time. Done.</p>
              </div>
              <button onClick={() => setShowGoogle(true)} className="mt-4 flex items-center justify-center gap-2 w-full py-3 rounded-button bg-blue-600 text-white font-extrabold text-lg shadow-button hover:opacity-90"><Search className="w-5 h-5" /> Show me how 👆</button>
            </div>
          </GradWrap>

          {/* WAY 3 · WEB */}
          <GradWrap grad="from-rose-500 to-rose-600">
            <LabelBar grad="from-rose-500 to-rose-600">Way 3 · On the Web</LabelBar>
            <div className="p-5">
              <div className="flex items-start gap-3">
                <Globe className="w-8 h-8 text-rose-600 shrink-0 mt-0.5" />
                <p className="text-base font-semibold text-ink leading-snug">Go to <b>slikbarberlounge.com</b> → tap <b>Book</b>. No account, about 30 seconds.</p>
              </div>
              <Link to="/book?tour=1" className="mt-4 flex items-center justify-center gap-2 w-full py-3 rounded-button bg-rose-600 text-white font-extrabold text-lg shadow-button hover:opacity-90"><Calendar className="w-5 h-5" /> Walk me through it 👆</Link>
              <Link to="/book" className="mt-2 block text-center text-sm font-semibold text-rose-600 hover:underline">or just start booking →</Link>
            </div>
          </GradWrap>

          {/* BE REMEMBERED — your SLIK account (portal), saved to your phone. The real "Fresha remembered
              you" replacement. Sits after the 3 ways, before guest. */}
          <GradWrap grad="from-slik-navy to-slik-purple">
            <LabelBar grad="from-slik-navy to-slik-purple">Be Remembered · Your Account</LabelBar>
            <div className="p-4">
              <p className="text-base font-semibold text-ink leading-snug">Want SLIK to <b>remember you</b> — your cuts, rewards &amp; family? Set up your free account, then save it to your phone.</p>
              <a href={PORTAL_URL} className="mt-3 flex items-center justify-center gap-2 w-full py-3.5 rounded-button bg-slik-navy text-white font-extrabold text-lg shadow-button hover:opacity-90 active:scale-[.99] transition-all"><LogIn className="w-5 h-5" /> Sign in / Create account</a>
              <p className="mt-2 text-center text-sm font-semibold text-slik-purple">Been here before? <b>Claim your account</b> to bring your history + rewards.</p>
            </div>
            <div className="border-t-2 border-dashed border-slik-purple/30 bg-cream/80 px-4 py-4">
              <div className="flex items-center gap-2 text-ink font-extrabold text-base"><Share className="w-5 h-5 text-slik-purple" /> Then save SLIK to your phone 👇</div>
              <p className="mt-1.5 text-sm font-medium text-gray-700">Add it to your home screen = <b>your SLIK, always remembered</b>. No app download needed.</p>
              <ul className="mt-2 text-sm text-gray-800 space-y-2 font-semibold">
                <li className="flex items-center gap-1.5 flex-wrap"><b>iPhone:</b> tap <Share className="w-4 h-4 text-slik-purple shrink-0" /> <b>Share</b> → <b>Add to Home Screen</b></li>
                <li className="flex items-center gap-1.5 flex-wrap"><b>Android:</b> tap <MoreVertical className="w-4 h-4 text-slik-purple shrink-0" /> <b>menu</b> → <b>Add to Home screen</b></li>
              </ul>
            </div>
          </GradWrap>

          {/* FOOTER */}
          <div className="bg-white rounded-2xl shadow-card p-5 text-center mt-2">
            <Scissors className="w-5 h-5 text-slik-purple mx-auto mb-2" />
            <div className="flex items-center justify-center gap-2 text-base text-ink font-bold"><MapPin className="w-5 h-5 text-slik-purple" /> 184 Columbia Turnpike, Florham Park, NJ</div>
            {phone && <a href={`tel:${phone}`} className="mt-2 inline-flex items-center gap-2 text-base text-slik-purple font-bold"><Phone className="w-5 h-5" /> {phone}</a>}
            {hours.length > 0 && (
              <div className="mt-3 text-sm text-gray-500 space-y-0.5">
                {hours.map((hl, i) => (<div key={i}><span className="font-semibold text-gray-700">{hl.label}</span> · {hl.value}</div>))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Way-1 (Google) animated walkthrough */}
      <GoogleWalkthrough open={showGoogle} onClose={() => setShowGoogle(false)} />
    </div>
  );
};

export default AppLandingPage;
